import { StaticCartProvider, useStaticCart } from "@/contexts/StaticCartContext";
import { StaticCartItem } from "@/components/cart/StaticCartItem";
import { StaticCartSummary } from "@/components/cart/StaticCartSummary";
import { CountdownTimer } from "@/components/cart/CountdownTimer";
import { ScrollProgressBar } from "@/components/cart/ScrollProgressBar";
import LandingFooter from "@/components/landing/LandingFooter";
import { Badge } from "@/components/ui/badge";
import { Star, Clock, Users, Gift, Zap } from "lucide-react";

function CarrinhoLandingContent() {
  const { items, bonusCount, discount } = useStaticCart();

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-bg-primary via-dark-bg-secondary to-dark-bg-tertiary dark relative overflow-hidden">
      <ScrollProgressBar />
      
      {/* Floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-dark-accent/10 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header da Página com Timer */}
        <div className="text-center mb-12">
          {/* Timer de Countdown */}
          <div className="flex justify-center mb-6">
            <CountdownTimer 
              initialMinutes={15}
              onExpire={() => console.log('Oferta expirada!')}
              data-testid="cart-countdown-timer"
            />
          </div>

          <Badge variant="secondary" className="mb-6 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 text-yellow-200 border border-yellow-400/30 px-6 py-2 text-lg backdrop-blur-sm">
            <Gift className="w-5 h-5 mr-2" />
            OFERTA LIMITADA
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent mb-6 leading-tight">
            Seu Mapa Numerológico
            <span className="block bg-gradient-to-r from-dark-accent via-primary to-dark-accent-glow bg-clip-text text-transparent">
              Completo
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
            Descubra seus números pessoais e receba <span className="text-dark-accent font-semibold">{bonusCount} produtos extras</span> totalmente grátis!
          </p>
          
          {/* Indicadores de Urgência Premium */}
          <div className="flex flex-wrap justify-center gap-8 mt-8">
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10">
              <Clock className="w-5 h-5 text-dark-accent" />
              <span className="text-gray-300 font-medium">Oferta por tempo limitado</span>
            </div>
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10">
              <Users className="w-5 h-5 text-dark-accent" />
              <span className="text-gray-300 font-medium">+5.000 clientes atendidos</span>
            </div>
            <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span className="text-gray-300 font-medium">4.9/5 avaliação</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lista de Produtos */}
          <div className="lg:col-span-2">
            <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm">
              <div className="p-8 border-b border-dark-border">
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="w-6 h-6 text-dark-accent" />
                  <h2 className="text-2xl font-bold text-white">
                    Seu Pacote Completo
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-dark-accent/20 text-dark-accent border-dark-accent/30">
                    {items.length} produtos
                  </Badge>
                  <span className="text-gray-300">
                    1 produto principal + {bonusCount} bônus gratuitos
                  </span>
                </div>
              </div>
              
              <div className="divide-y divide-dark-border">
                {items.map((item) => (
                  <StaticCartItem key={item.id} item={item} />
                ))}
              </div>
              
              {/* Destaque da Economia */}
              <div className="p-8 bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-emerald-600/10 border-t border-dark-border">
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent mb-3">
                    Você está economizando R$ {discount},00
                  </div>
                  <p className="text-lg text-gray-300">
                    Ao adquirir agora, você recebe <span className="text-emerald-400 font-semibold">{bonusCount} produtos extras</span> sem custo adicional!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo e Checkout */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              <StaticCartSummary />
              
              {/* Garantias Premium */}
              <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm p-6" data-testid="cart-garantias-section">
                <h3 className="font-bold text-white mb-6 text-lg flex items-center gap-2">
                  <Star className="w-5 h-5 text-dark-accent" />
                  Garantias
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full animate-pulse"></div>
                    <span className="text-gray-300">Entrega imediata por email</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full animate-pulse"></div>
                    <span className="text-gray-300">Suporte especializado 24/7</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full animate-pulse"></div>
                    <span className="text-gray-300">Pagamento 100% seguro</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full animate-pulse"></div>
                    <span className="text-gray-300">Acesso vitalício aos produtos</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full animate-pulse"></div>
                    <span className="text-gray-300">Garantia de satisfação</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <LandingFooter />
    </div>
  );
}

export default function CarrinhoLanding() {
  return (
    <StaticCartProvider>
      <CarrinhoLandingContent />
    </StaticCartProvider>
  );
}